import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type MemberProfile } from '../lib/api';
import { useMe, useToast } from '../hooks';
import { ErrorBox, Loading, PageHead } from '../ui';
import { leaveForm } from '../lib/nav';
import { useBarTitle } from '../barTitle';

/** Admins: add a member or edit names, contact data and role. */
export default function MemberForm() {
  const { id } = useParams();
  const editId = id ? Number(id) : null;
  useBarTitle(editId ? 'Mitglied bearbeiten' : 'Mitglied hinzufügen'); // also while loading
  const { data: me } = useMe();
  const src = useQuery({
    queryKey: ['member', editId],
    queryFn: () => api<MemberProfile>(`/members/${editId}`),
    enabled: !!editId,
  });
  if (me && me.role !== 'admin') return <PageHead title="Nur für Admins" />;
  if (src.error) return <ErrorBox error={src.error} />;
  if (editId && !src.data) return <Loading />;
  return <MemberFormBody key={editId ?? 'new'} m={src.data} self={editId === me?.id} />;
}

function MemberFormBody({ m, self }: { m?: MemberProfile; self: boolean }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const c = m?.contact;
  const [f, setF] = useState({
    first_name: m?.first_name ?? '',
    last_name: m?.last_name ?? '',
    email: c?.email ?? '',
    phone: c?.phone ?? '',
    street: c?.street ?? '',
    house_number: c?.house_number ?? '',
    zip: c?.zip ?? '',
    city: c?.city ?? '',
    role: m?.role ?? 'member',
  });
  const [invite, setInvite] = useState(true); // new members: invitation by mail
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const field = (k: keyof typeof f, label: string, props: Record<string, unknown> = {}) => (
    <label className="fl">
      <span>{label}</span>
      <input value={f[k]} onChange={e => setF({ ...f, [k]: e.target.value })} {...props} />
    </label>
  );
  async function submit(ev: FormEvent) {
    ev.preventDefault();
    setError('');
    if (!f.first_name.trim() || !f.last_name.trim()) return setError('Bitte Vor- und Nachname angeben.');
    const body = self ? (({ role: _role, ...rest }) => rest)(f) : f; // own role cannot be changed
    setBusy(true);
    try {
      const r = m
        ? await api(`/members/${m.id}`, { method: 'PATCH', body })
        : await api<{ id: number; invited: boolean }>('/members', { method: 'POST', body: { ...body, invite } });
      void qc.invalidateQueries({ queryKey: ['members'] });
      void qc.invalidateQueries({ queryKey: ['member'] });
      if (self) void qc.invalidateQueries({ queryKey: ['me'] }); // own name in the top bar
      toast(
        m
          ? 'Gespeichert'
          : (r as { invited?: boolean }).invited
            ? 'Mitglied angelegt – Einladung per E-Mail verschickt'
            : 'Mitglied angelegt',
      );
      if (m) leaveForm(nav, `/mitglieder/${m.id}`);
      else void nav(`/mitglieder/${(r as { id: number }).id}`, { replace: true });
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }
  return (
    <>
      <PageHead title={m ? 'Mitglied bearbeiten' : 'Mitglied hinzufügen'} />
      <form className="form panel" onSubmit={e => void submit(e)} noValidate style={{ maxWidth: 720 }}>
        <div className="grid2">
          {field('first_name', 'Vorname', { required: true, maxLength: 60 })}
          {field('last_name', 'Nachname', { required: true, maxLength: 60 })}
        </div>
        <div className="grid2">
          {field('email', 'E-Mail (Anmeldung)', { type: 'email', inputMode: 'email', maxLength: 254 })}
          {field('phone', 'Telefon', { inputMode: 'tel', maxLength: 40 })}
        </div>
        <div className="grid3">
          {field('street', 'Straße', { maxLength: 120 })}
          {field('house_number', 'Nr.', { maxLength: 20 })}
        </div>
        <div className="grid3">
          {field('zip', 'PLZ', { inputMode: 'numeric', maxLength: 10 })}
          {field('city', 'Ort', { maxLength: 80 })}
        </div>
        <label className="fl" style={{ maxWidth: 260 }}>
          <span>Rolle</span>
          <select
            value={f.role}
            disabled={self}
            onChange={e => setF({ ...f, role: e.target.value as 'member' | 'admin' })}
          >
            <option value="member">Mitglied</option>
            <option value="admin">Admin</option>
          </select>
        </label>
        {!m && (
          <label className="set-row" style={{ cursor: 'pointer', maxWidth: 520 }}>
            <div>
              <b>Einladung per E-Mail schicken</b>
              <p>Kurze Begrüßung mit Link zum Sportplaner – die Anmeldung läuft wie immer mit einem Code.</p>
            </div>
            <span className="switch">
              <input
                type="checkbox"
                checked={invite && !!f.email.trim()}
                disabled={!f.email.trim()}
                onChange={e => setInvite(e.target.checked)}
                aria-label="Einladung per E-Mail schicken"
              />
              <span />
            </span>
          </label>
        )}
        <p className="note">
          Mit der E-Mail-Adresse meldet sich die Person an. {self ? 'Deine eigene Rolle kannst du nicht ändern.' : ''}
        </p>
        {error && (
          <p className="err" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={() => void nav(-1)}>
            Abbrechen
          </button>
          <button className="btn btn-primary" disabled={busy}>
            {m ? 'Speichern' : 'Mitglied anlegen'}
          </button>
        </div>
      </form>
    </>
  );
}
